import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getAuditHistory, AuditHistoryItem } from '../api'

interface OffersAuditHistoryProps {
  onDeleteRequest: (jobId: string) => void
  onSelectJob?: (item: AuditHistoryItem) => void
  embedded?: boolean
  deletedJobId?: string | null
}

const PAGE_SIZE = 20

export default function OffersAuditHistory({ onDeleteRequest, onSelectJob, embedded, deletedJobId }: OffersAuditHistoryProps) {
  const { isAuthenticated } = useAuth()
  const [filter, setFilter] = useState<'mine' | 'all'>('mine')
  const [historyItems, setHistoryItems] = useState<AuditHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE)

  const loadHistory: (retryCount?: number) => Promise<void> = useCallback(async (retryCount = 0) => {
    setIsLoading(true)
    setError(null)
    setDisplayCount(PAGE_SIZE) // Reset pagination on every fresh load
    try {
      const result = await getAuditHistory(filter)
      setHistoryItems(result.jobs)
    } catch {
      if (retryCount < 1) {
        await new Promise(resolve => setTimeout(resolve, 1000))
        return loadHistory(retryCount + 1)
      }
      setError('Could not load history. This may be a temporary issue. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [filter])

  useEffect(() => {
    if (isAuthenticated) {
      loadHistory()
    }
  }, [isAuthenticated, loadHistory])

  // Remove deleted item from list without needing a refresh
  useEffect(() => {
    if (deletedJobId) {
      setHistoryItems((prev) => prev.filter((item) => item.jobId !== deletedJobId))
    }
  }, [deletedJobId])

  const handleFilterChange = (newFilter: 'mine' | 'all') => {
    if (newFilter !== filter) {
      setFilter(newFilter)
    }
  }

  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (seconds < 60) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days === 1) return '1d ago'
    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return new Date(timestamp).toLocaleDateString()
  }

  const handleSelectJob = (item: AuditHistoryItem) => {
    if (item.status !== 'completed' && item.status !== 'failed') return
    onSelectJob?.(item)
  }

  return (
    <div className={embedded ? '' : 'border border-linear-border rounded-lg bg-linear-bg-card overflow-hidden'}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-linear-border sticky top-0 z-10 bg-linear-bg-sidebar">
        <div className="flex items-center gap-3">
          {/* Segmented toggle */}
          <div className="flex bg-linear-bg p-0.5 rounded border border-linear-border">
            <button
              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                filter === 'mine'
                  ? 'bg-linear-bg-card text-linear-text shadow-sm font-medium'
                  : 'text-linear-text-muted hover:text-linear-text-secondary'
              }`}
              onClick={() => handleFilterChange('mine')}
            >
              Mine
            </button>
            <button
              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                filter === 'all'
                  ? 'bg-linear-bg-card text-linear-text shadow-sm font-medium'
                  : 'text-linear-text-muted hover:text-linear-text-secondary'
              }`}
              onClick={() => handleFilterChange('all')}
            >
              All
            </button>
          </div>
        </div>

        {/* Refresh button */}
        <button
          onClick={() => loadHistory()}
          className="text-linear-text-muted hover:text-linear-text transition-colors"
          title="Refresh"
          disabled={isLoading}
        >
          <svg className={`w-5 h-5 transition-transform ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Content area */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="spinner"></div>
          <span className="ml-2 text-linear-text-muted text-sm">Loading...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-error/10 border-b border-error/30 text-error text-sm">
          {error}
          <button
            onClick={() => loadHistory()}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      ) : historyItems.length === 0 ? (
        <div className="p-4 text-linear-text-muted text-sm text-center">
          {filter === 'mine'
            ? "You haven't run any audits yet."
            : 'No audits have been run yet.'}
        </div>
      ) : (
        <div>
          {historyItems.slice(0, displayCount).map((item) => {
            const isClickable = item.status === 'completed' || item.status === 'failed'
            return (
              <div
                key={item.jobId}
                role="button"
                tabIndex={isClickable ? 0 : -1}
                className={`group flex items-center px-4 py-3 border-b border-linear-border transition-colors gap-2 ${
                  isClickable
                    ? 'hover:bg-linear-bg cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => handleSelectJob(item)}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && isClickable) { e.preventDefault(); handleSelectJob(item) } }}
              >
                {/* Col 1: App badge + status + attribution (flex, truncates) */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-linear-text group-hover:text-linear-accent transition-colors">
                      {item.application} Audit
                    </span>
                    {item.status !== 'completed' && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        item.status === 'processing' ? 'bg-info/10 text-info' :
                        item.status === 'failed' ? 'bg-error/10 text-error' :
                        'bg-warning/10 text-warning'
                      }`}>
                        {item.status}
                      </span>
                    )}
                  </div>
                  {filter !== 'mine' && item.userName && (
                    <span className="text-[10px] text-linear-text-muted truncate block">by {item.userName}</span>
                  )}
                </div>

                {/* Col 2: Results summary (colored dots, like type breakdown) - hidden on narrow screens */}
                <span className="history-coverage-stats flex items-center gap-2 text-[11px] text-linear-text-muted flex-shrink-0 w-[90px]">
                  {item.resultsSummary && (
                    <>
                      {item.resultsSummary.passed > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                          {item.resultsSummary.passed}
                        </span>
                      )}
                      {item.resultsSummary.warnings > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-warning"></span>
                          {item.resultsSummary.warnings}
                        </span>
                      )}
                      {(item.resultsSummary.failed + (item.resultsSummary.errors || 0)) > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-error"></span>
                          {item.resultsSummary.failed + (item.resultsSummary.errors || 0)}
                        </span>
                      )}
                    </>
                  )}
                </span>

                {/* Col 3: Date (fixed width, right-aligned) */}
                <span className="text-[11px] text-linear-text-muted flex-shrink-0 w-[70px] text-right whitespace-nowrap">
                  {formatRelativeTime(item.createdAt)}
                </span>

                {/* Col 4: Actions (fixed width, always present for alignment) */}
                <div className="flex items-center flex-shrink-0 w-[28px] justify-end">
                  {item.isOwner && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteRequest(item.jobId)
                      }}
                      className="p-1 hover:bg-linear-bg-sidebar rounded text-linear-text-muted hover:text-error transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete"
                      aria-label="Delete audit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )
          })}
          {/* Load more / pagination footer */}
          {historyItems.length > displayCount && (
            <>
              <div className="px-4 pt-2 pb-1 flex justify-end">
                <span className="text-[10px] text-linear-text-muted">
                  Showing {displayCount} of {historyItems.length}
                </span>
              </div>
              <button
                onClick={() => setDisplayCount((c) => Math.min(c + PAGE_SIZE, historyItems.length))}
                className="w-full py-2.5 text-[12px] text-linear-text-muted hover:text-linear-accent hover:bg-linear-bg transition-colors border-t border-linear-border"
              >
                Load {Math.min(PAGE_SIZE, historyItems.length - displayCount)} more
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
